import { createApp, type Directive, type Plugin } from 'vue';
import { createPinia } from 'pinia';
import { defineRule } from 'vee-validate';
import { all as rules } from '@vee-validate/rules';
import { LicenseManager } from 'ag-grid-enterprise';

import PrimeVue from 'primevue/config';
import createAppRouter from '@/router/router.ts';
import App from './App.vue';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import AutoComplete from 'primevue/autocomplete';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import Calendar from 'primevue/calendar';
import Checkbox from 'primevue/checkbox';
import ConfirmationService from 'primevue/confirmationservice';
import ConfirmDialog from 'primevue/confirmdialog';
import Dialog from 'primevue/dialog';
import DialogService from 'primevue/dialogservice';
import Dropdown from 'primevue/dropdown';
import FileUpload from 'primevue/fileupload';
import InputNumber from 'primevue/inputnumber';
import InputSwitch from 'primevue/inputswitch';
import InputText from 'primevue/inputtext';
import Listbox from 'primevue/listbox';
import MultiSelect from 'primevue/multiselect';
import OverlayPanel from 'primevue/overlaypanel';
import ProgressSpinner from 'primevue/progressspinner';
import Tag from 'primevue/tag';
import Textarea from 'primevue/textarea';
import Toast from 'primevue/toast';
import ToastService from 'primevue/toastservice';
import Message from 'primevue/message';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import FloatLabel from 'primevue/floatlabel';
import Tooltip from 'primevue/tooltip';
import { ModuleRegistry, AllEnterpriseModule } from 'ag-grid-enterprise';

import { initializeApplicationInsights } from '@/util/applicationInsights.js';

import 'primevue/resources/themes/aura-light-green/theme.css';
import 'primeicons/primeicons.css';
import 'primevue/resources/primevue.min.css';
import '@/assets/sass/base.scss';
import '@/assets/sass/vendors/prime/theme/bair.scss';
import '@/assets/main.css';
import {loginRequest, msalInstance} from "@/app/auth/msal-config.ts";
import {EventType} from "@azure/msal-browser";
import AllotmentApiClientPlugin from "@/plugins/services.ts";
import {VueNavigationClient} from "@/router/router-helpers.ts";

function asPlugin(module: unknown): Plugin {
  const pluginModule = module as Plugin & { default?: Plugin };
  return pluginModule.default ?? pluginModule;
}

function asDirective(module: unknown): Directive {
  const directiveModule = module as Directive & { default?: Directive };
  return directiveModule.default ?? directiveModule;
}

// Register vee-validate rules
Object.keys(rules).forEach((rule) => {
  const ruleValidator = rules[rule as keyof typeof rules];
  if (ruleValidator) {
    defineRule(rule, ruleValidator);
  }
});

// Set AG Grid license
LicenseManager.setLicenseKey(import.meta.env.VITE_AGGRID_LICENSE);
ModuleRegistry.registerModules([
  AllEnterpriseModule
]);

// Create and configure Vue app
const app = createApp(App);

// Install core plugins
app
  .use(createPinia())
  .use(AllotmentApiClientPlugin)
  .use(asPlugin(PrimeVue))
  .use(asPlugin(ConfirmationService))
  .use(asPlugin(DialogService))
  .use(asPlugin(ToastService))
  .directive('tooltip', asDirective(Tooltip))
  .component('p-accordion', Accordion)
  .component('p-accordion-tab', AccordionTab)
  .component('p-autocomplete', AutoComplete)
  .component('p-badge', Badge)
  .component('p-button', Button)
  .component('p-calendar', Calendar)
  .component('p-checkbox', Checkbox)
  .component('p-confirm-dialog', ConfirmDialog)
  .component('p-dialog', Dialog)
  .component('p-dropdown', Dropdown)
  .component('p-file-upload', FileUpload)
  .component('p-input-number', InputNumber)
  .component('p-input-switch', InputSwitch)
  .component('p-input-text', InputText)
  .component('p-listbox', Listbox)
  .component('p-multiselect', MultiSelect)
  .component('p-overlay', OverlayPanel)
  .component('p-progress-spinner', ProgressSpinner)
  .component('p-tag', Tag)
  .component('p-message', Message)
  .component('p-textarea', Textarea)
  .component('p-tabview', TabView)
  .component('p-tabpanel', TabPanel)
  .component('p-floatlabel', FloatLabel)
  .component('p-toast', Toast);

initializeApp().then()

// Initialize Application Insights telemetry
initializeApplicationInsights({
  src: 'https://js.monitor.azure.com/scripts/b/ai.3.gbl.min.js',
  crossOrigin: 'anonymous',
  onInit: (aiSdk: any) => {
    const telemetryInitializer = (envelope: any) => {
      envelope.tags['ai.cloud.role'] =
        '[' + import.meta.env.VITE_AI_ServiceNamespace + ']/' + import.meta.env.VITE_AI_ServiceName;
    };
    aiSdk.addTelemetryInitializer(telemetryInitializer);
  },
  cfg: {
    connectionString: import.meta.env.VITE_AI_ConnectionString,
  },
});

async function initializeApp() {
  await msalInstance.initialize();
  const router = createAppRouter()
  msalInstance.setNavigationClient(new VueNavigationClient(router))

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as any;
      if (payload.account) {
        msalInstance.setActiveAccount(payload.account);
      }
    }
  });

  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      msalInstance.setActiveAccount(response.account);
    } else {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && accounts[0]) {
        msalInstance.setActiveAccount(accounts[0]);
      } else {
        await msalInstance.loginRedirect(loginRequest);
        return;
      }
    }
  } catch (error) {
    console.error('MSAL Redirect Error:', error);
  }

  app.use(router)
  app.mount('#app')
}
